import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import getPermissions from '../../../api/oc_product/sites/permissions/gets/getPermissions';
import { setLanguage } from '../../../redux/language/Language';
// import getUserById from '../../../api/private/users_management/gets/getUserById';
import { setSelectedSitePermissions } from '../../../redux/sites/sitePermissions';
import { setSelectedSite } from '../../../redux/user/User';

function combineSites(data) {
  const sites = [];
  if (Array.isArray(data)) {
    data.forEach((obj) => {
      obj.Sites.forEach((site) => {
        sites.push({
          ...site,
          parent: obj.Name,
          SiteGroupId: obj.SiteGroupId,
        });
      });
    });
    return sites;
  }
  return [];
}

function SiteSelect({ variant }) {
  const dispatch = useDispatch();

  const selectedSite = useSelector((state) => state.user?.loginResponse?.selectedSite);
  // const userId = useSelector((state) => state.user?.userProfile?.id);
  const user = useSelector((state) => state.user.userProfile);
  const fascias = useSelector((state) => state.fascias);
  const languages = useSelector((state) => state.languages);
  const usersSites = useSelector((state) => state.user?.userProfile?.sid);
  const [, setSearchParams] = useSearchParams();

  const [selected, setSelected] = React.useState(null);
  const [selectedLanguage, setSelectedLanguage] = React.useState(languages?.[0] || null);
  const [options, setOptions] = React.useState([]);
  const [siteId, setSiteId] = React.useState();
  // Track if site was just selected by user to prevent useEffect from overriding
  const userSelectedSiteRef = React.useRef(false);
  // Track if initial site setup is complete
  const isInitializedRef = React.useRef(false);

  // Set initial language
  React.useEffect(() => {
    if (selectedLanguage) {
      dispatch(setLanguage(selectedLanguage));
    }
  }, []);

  const setSiteFromQuery = async (combined, usersSidsArray) => {
    // Skip if user just selected a site to prevent race condition
    if (userSelectedSiteRef.current) {
      userSelectedSiteRef.current = false;
      return;
    }

    // Read siteCode directly from URL to avoid stale searchParams
    const urlParams = new URLSearchParams(window.location.search);
    const siteCodeFromUrl = urlParams.get('siteCode');
    const foundSite = combined.find((site) => site.SiteCode === siteCodeFromUrl);
    if (foundSite && usersSidsArray?.includes(foundSite.SiteId)) {
      dispatch(setSelectedSite(foundSite));
      setSelected(foundSite);
      setSiteId(foundSite.SiteId);
    } else if (selectedSite && !siteCodeFromUrl) { // Set site from redux if there is no query param
      setSearchParams((params) => {
        params.set('siteCode', selectedSite.SiteCode);
        return params;
      });
      setSelected(selectedSite);
      setSiteId(selectedSite.SiteId);
    } else {
      const defaultJD = combined.find((site) => site.SiteId === usersSidsArray[0]);
      if (defaultJD) {
        dispatch(setSelectedSite(defaultJD));
        setSearchParams((params) => {
          params.set('siteCode', defaultJD.SiteCode);
          return params;
        });
        setSelected(defaultJD);
        setSiteId(defaultJD.SiteId);
      }
    }
  };

  React.useEffect(() => {
    if (siteId) {
      getPermissions(siteId)
        .then((res) => dispatch(setSelectedSitePermissions(res.data)))
        .catch((err) => console.error(err));
    }
  }, [siteId]);

  // Wait for both fascias and usersSites to be available before initializing
  React.useEffect(() => {
    if (fascias.length > 0 && usersSites) {
      try {
        const usersSidsArray = JSON.parse(usersSites).sort((a, b) => a - b);
        const combined = combineSites(fascias);

        // Only run setSiteFromQuery if not already initialized
        if (!isInitializedRef.current) {
          isInitializedRef.current = true;
          setSiteFromQuery(combined, usersSidsArray);
        }

        // Filter options based on user's accessible sites
        const filtered = combined.filter((obj) => usersSidsArray.includes(obj.SiteId));
        setOptions(filtered);
      } catch (error) {
        console.error('Error parsing usersSites:', error);
      }
    }
  }, [fascias, usersSites]);

  const handleOptionSelected = (event, value) => {
    // Guard against null/undefined value
    if (!value || !value.SiteCode || !value.SiteId) {
      return;
    }

    // Mark that user is selecting a site to prevent setSiteFromQuery from interfering
    userSelectedSiteRef.current = true;

    // Update local state
    setSelected(value);
    setSiteId(value.SiteId);

    // Update Redux
    dispatch(setSelectedSite(value));

    // Fetch permissions for the new site
    getPermissions(value.SiteId)
      .then((res) => dispatch(setSelectedSitePermissions(res.data)))
      .catch((err) => console.error(err))
      .finally(() => {
        // Reset the flag after all operations complete
        userSelectedSiteRef.current = false;
      });

    // Update URL with new siteCode while preserving other params
    // Using a new URLSearchParams object ensures React Router's state is fully updated
    const newParams = new URLSearchParams(window.location.search);
    newParams.set('siteCode', value.SiteCode);
    setSearchParams(newParams, { replace: true });
  };

  const handleLanguageOptionSelected = (event, value) => {
    setSelectedLanguage(value);
    dispatch(setLanguage(value));
  };

  const groupStyles = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#E9F2F8',
    fontWeight: 'bold',
    padding: '5px 10px',
    position: 'sticky',
    top: '-8px',
    zIndex: '1',
  };

  const renderGroup = (params) => [
    <div key={params.key} style={groupStyles}>
      {params.group}
    </div>,
    params.children,
  ];

  // Check if the URL contains a product ID so we can disable Autocomplete if it does
  const isProductPage = /\/product-info\/products\/\d+/.test(window.location.href);
  const hideSettings = window.location.href.includes('settings') || window.location.href.includes('image-checker') || user?.user_groupName === 'ImageChecker' || window.location.href.includes('colour-matching');
  const showLanguages = window.location.href.includes('colour-matching');
  return (
    <div>
      {!hideSettings && (
        <Autocomplete
          value={selected}
          options={options}
          groupBy={(option) => option.parent}
          getOptionLabel={(option) => option?.Name || ''}
          onChange={handleOptionSelected}
          renderInput={(params) => (
            <TextField {...params} label="Select Site" variant={variant} />
          )}
          size="small"
          filterOptions={createFilterOptions({
            limit: 100,
          })}
          renderGroup={renderGroup}
          className="w-[200px]"
          disableClearable
          disabled={isProductPage}
          sx={{
            '& .Mui-disabled': {
              WebkitTextFillColor: 'rgba(255, 255, 255, 0.6) !important', // Specifically targets disabled state
              color: 'rgba(255, 255, 255, 0.6) !important',
            },
            '& .MuiInputBase-input': {
              color: 'rgba(255, 255, 255, 0.6)',
              WebkitTextFillColor: 'rgba(255, 255, 255, 0.6) !important', // Force override in Browser
            },
            '& .MuiInput-underline:before': {
              borderBottomColor: 'white',
            },
            '.css-ghsjzk-MuiInputBase-root-MuiInput-root:after': {
              color: 'white',
            },
            '& .MuiFormControl-root label': {
              color: 'white',
            },
            '& .MuiFormControl-root input .MuiInputBase-input': {
              color: 'white',
            },
            '& .MuiFormControl-root button': {
              color: 'white',
            },
          }}

        />
      )}
      {showLanguages && (
        <Autocomplete
          value={selectedLanguage}
          options={languages.map((obj) => ({
            ...obj,
            Name: obj.LanguageName,
          }))}
          getOptionLabel={(option) => option?.LanguageName || ''}
          onChange={handleLanguageOptionSelected}
          renderInput={(params) => (
            <TextField {...params} label="Select Language" variant={variant} />
          )}
          defaultValue={languages[0]}
          size="small"
          // filterOptions={createFilterOptions({
          //   limit: 100,
          // })}
          className="w-[200px]"
          disableClearable
          sx={{
            '& .Mui-disabled': {
              WebkitTextFillColor: 'rgba(255, 255, 255, 0.6) !important', // Specifically targets disabled state
              color: 'rgba(255, 255, 255, 0.6) !important',
            },
            '& .MuiInputBase-input': {
              color: 'white',
              WebkitTextFillColor: 'rgba(255, 255, 255, 0.6) !important', // Force override in Browser
            },
            '& .MuiInput-underline:before': {
              borderBottomColor: 'white',
            },
            '.css-ghsjzk-MuiInputBase-root-MuiInput-root:after': {
              color: 'white',
            },
            '& .MuiFormControl-root label': {
              color: 'white',
            },
            '& .MuiFormControl-root input .MuiInputBase-input': {
              color: 'white',
            },
            '& .MuiFormControl-root button': {
              color: 'white',
            },
          }}

        />
      )}
    </div>
  );
}

export default SiteSelect;
